import React, { useMemo, useCallback } from 'react';
import { message, Button, Space, Tooltip } from 'antd';
import copy from 'copy-to-clipboard';
import { CopyOutlined, CloseOutlined, PlusOutlined, InfoCircleOutlined } from '@ant-design/icons';
import ExportSvg from '@src/icons/export.svg';
import { useLatest } from 'react-use';
import { cloneDeep, get } from 'lodash';
import * as ReactDnD from 'react-dnd';
import * as ReactDndHtml5Backend from 'react-dnd-html5-backend';
import { QueryBuilder, update, findPath, add, generateID } from 'react-querybuilder';
import { QueryBuilderDnD } from '@react-querybuilder/dnd';
import { QueryBuilderAntD } from '@react-querybuilder/antd';
import DimensionField from './DimensionField';
import DimensionValue from './DimensionValue';
import RuleGroup from './RuleGroup';
import Operator from './Operator';
import {
  getOperators,
  getFieldSchema,
  generateRuleByFieldSchema,
  generateGroupByFieldSchema,
  getGroupedFieldsByCategory,
  allowMultiSelection,
} from './utils';
import * as Styled from './styled';
import * as Types from '../../../types';
import {
  type QueryBuilderContext,
  type Query,
  type QueryBuilderFieldSchema,
  type QueryBuilderGroupRule,
  type QueryBuilderRule,
} from './types';
import 'react-querybuilder/dist/query-builder.css';
import { DataSource } from '../../utils';

export interface FilterRulesBuilderProps {
  className?: string;
  dataSource: DataSource;
  query: Query;
  disabledOperators?: string[];
  showError?: boolean;
  initialField?: QueryBuilderFieldSchema;
  fields: QueryBuilderFieldSchema[];
  filterRules?: Types.Filter['rule'];
  drilldownLimit?: number;
  onChange: (query: Query) => void;
  showAddRuleButton?: boolean;
  showAddGroupButton?: boolean;
}

const operators = getOperators();

const FilterRulesBuilder = (builderProps: FilterRulesBuilderProps) => {
  const {
    className,
    dataSource,
    query,
    showError,
    fields,
    drilldownLimit = 900,
    onChange,
    showAddRuleButton = true,
    showAddGroupButton = true,
  } = builderProps;
  const initialField = builderProps.initialField || fields[0];
  const groupedFields = useMemo(() => getGroupedFieldsByCategory(fields), [fields]);

  const reachDrilldownLimit = (query.rules || []).length >= drilldownLimit;

  const handleQueryChangeRef = useLatest((q: Query) => {
    // Clean group with empty rules
    const nextQ = { ...q };
    nextQ.rules = nextQ.rules.filter((it) => {
      const isEmptyGroup =
        (it as QueryBuilderGroupRule).rules && !(it as QueryBuilderGroupRule).rules.length;
      return !isEmptyGroup;
    });

    // // Put default field when the query is empty
    // if (!nextQ.rules?.length) {
    //   const newFieldRule = generateRuleByFieldSchema(initialField);
    //   nextQ.rules = [newFieldRule];
    // }
    onChange(nextQ);
  });

  const onKeyChange = useCallback<QueryBuilderContext['onKeyChange']>(
    (key, path) => {
      const newQuery = update(query, 'key' as any, key, path);
      handleQueryChangeRef.current(newQuery as Query);
    },
    [query, handleQueryChangeRef],
  );

  const onFieldChange = useCallback<QueryBuilderContext['onFieldChange']>(
    (_, path, schema) => {
      const newQuery = cloneDeep(query);
      const target = findPath(path, newQuery);
      if (!target) return;
      const newFieldRule = generateRuleByFieldSchema(schema);
      Object.assign(target, newFieldRule);
      handleQueryChangeRef.current(newQuery as Query);
    },
    [query, handleQueryChangeRef],
  );

  const onValueChange = useCallback<QueryBuilderContext['onValueChange']>(
    (data, path) => {
      const newQuery = update(query, 'value', data, path);
      handleQueryChangeRef.current(newQuery as Query);
    },
    [query, handleQueryChangeRef],
  );

  const onOperatorChange = useCallback<QueryBuilderContext['onOperatorChange']>(
    (operator, path) => {
      let newQuery = update(query, 'operator', operator, path);
      const target = findPath(path, newQuery) as QueryBuilderRule;

      // Multiple values should be split into each individual rule
      // when toggling from equals&contains to not_equals&not_contains for multiselection
      if (path?.length === 1 && !allowMultiSelection(operator) && target?.value.length > 1) {
        newQuery = cloneDeep(newQuery);
        // split multivalues into repeated rule with different rule value.
        const splitRules = target.value.map((it) => {
          return { ...target, value: [it], id: generateID() };
        });
        newQuery.rules.splice(path[0], 1, ...splitRules);
      }
      handleQueryChangeRef.current(newQuery as Query);
    },
    [query, handleQueryChangeRef],
  );

  const addRule = useCallback<QueryBuilderContext['addRule']>(
    (ruleOrGroup, parentPath) => {
      const newQuery = add(query, ruleOrGroup, parentPath ?? []);
      handleQueryChangeRef.current(newQuery);
    },
    [query, handleQueryChangeRef],
  );

  const handleAddAndRule = () => {
    if (reachDrilldownLimit) {
      message.warning(`Reached drilldown limit of ${drilldownLimit}`);
      return;
    }
    const rule = generateRuleByFieldSchema(initialField);
    addRule(rule, []);
  };

  const handleAddGroup = () => {
    if (reachDrilldownLimit) {
      message.warning(`Reached drilldown limit of ${drilldownLimit}`);
      return;
    }
    const orGroup = generateGroupByFieldSchema(initialField, 'or');
    addRule(orGroup, []);
  };

  const handleOnQueryChange = (q: Query) => {
    // React query builder will trigger onChange in first rendering.
    // Below code is to avoid this issue
    const isSameQuery = JSON.stringify(q) === JSON.stringify(query);
    if (isSameQuery) return;
    handleQueryChangeRef.current(q);
  };

  return (
    <Styled.Container className={className}>
      <QueryBuilderDnD dnd={{ ...ReactDnD, ...ReactDndHtml5Backend }}>
        <QueryBuilderAntD>
          <QueryBuilder
            autoSelectField
            showCloneButtons
            context={{
              dataSource,
              fields,
              initialField,
              showError,
              groupedFields,
              onFieldChange,
              onKeyChange,
              onOperatorChange,
              onValueChange,
              addRule,
            }}
            fields={fields}
            query={query}
            operators={operators}
            onQueryChange={(q) => handleOnQueryChange(q as Query)}
            controlElements={{
              fieldSelector: DimensionField,
              operatorSelector: Operator,
              valueEditor: DimensionValue,
              ruleGroup: RuleGroup,
              combinatorSelector: () => null,
              cloneGroupAction: () => null,
              removeGroupAction: () => null,
              addGroupAction: () => null,
              addRuleAction: () => null,
              removeRuleAction: (props) => {
                // if (query.rules?.length === 1 && !(query.rules[0] as any).rules) return null;

                return (
                  <Tooltip title="Remove filter rule">
                    <CloseOutlined onClick={props.handleOnClick} className="delete-icon" />
                  </Tooltip>
                );
              },
              cloneRuleAction: (props) => {
                const isValidFilterDimension = !!getFieldSchema(
                  get(props, ['ruleOrGroup', 'field']),
                  fields,
                );
                const isInvalidCustomTag =
                  !isValidFilterDimension && get(props, ['ruleOrGroup', 'field']) === 'TAG';

                const renderCopyValuesIcon = () => {
                  const values = get(props, ['ruleOrGroup', 'value'], []);
                  if (!values.length) return null;

                  const handleCopyValues = () => {
                    const valueStr = values.map((it) => it.label).join('\n');
                    if (copy(valueStr, { format: 'text/plain' })) {
                      message.info('Copied to clipboard');
                    }
                  };
                  return (
                    <Tooltip title="Values will be copied using a new line as the delimiter">
                      <CopyOutlined onClick={handleCopyValues} className="copy-icon" />
                    </Tooltip>
                  );
                };

                // invalid dimension don't have copy ability
                if (!isValidFilterDimension) {
                  return (
                    <>
                      <Tooltip
                        title={
                          isInvalidCustomTag
                            ? 'Invalid custom tag, please remove it'
                            : 'The dimension has been deleted, please remove it'
                        }
                      >
                        <InfoCircleOutlined style={{ color: '#D0021B' }} className="prompt-icon" />
                      </Tooltip>
                      {renderCopyValuesIcon()}
                    </>
                  );
                }

                const handleClone = (e) => {
                  const isFirstLevel = props.level === 1;
                  if (reachDrilldownLimit && isFirstLevel) {
                    message.warning(`Reached drilldown limit of ${drilldownLimit}`);
                    return;
                  }
                  props.handleOnClick(e);
                };
                return (
                  <>
                    <Tooltip title="Clone filter rule">
                      <ExportSvg onClick={handleClone} className="clone-icon" />
                    </Tooltip>
                    {renderCopyValuesIcon()}
                  </>
                );
              },
            }}
          />
        </QueryBuilderAntD>
      </QueryBuilderDnD>
      <Space style={{ margin: '10px 0 0 60px' }}>
        {/* showAddRuleButton={false} disables adding more conditions (e.g. single-condition mute rules) */}
        {showAddRuleButton && (
          <Button ghost type="primary" onClick={handleAddAndRule} icon={<PlusOutlined />}>
            AND
          </Button>
        )}
        {showAddGroupButton && (
          <Button ghost type="primary" onClick={handleAddGroup} icon={<PlusOutlined />}>
            OR Group
          </Button>
        )}
      </Space>
    </Styled.Container>
  );
};

export default React.memo(FilterRulesBuilder);
